import type { IsoTimestamp } from './common.js';
import type { Appointment } from './appointments.js';
import type { LeadOutcome } from './leadOutcome.js';
import type { AutonomyBlockReason } from '../lib/leadBot/mode.js';

// SMS Lead Bot Phase 0 — the shared client + server model.
//
// ⚠️⚠️ READ THIS BEFORE CHANGING ANYTHING HERE. Every other AI surface in this
// codebase is human-in-the-loop for CONVENIENCE: Claude drafts, an AM reads it,
// edits it, sends it, and a bad draft costs nothing. This bot sends SMS to a
// member of the public, on a client's behalf, with no undo. An invented price or
// finance rate reaches a consumer with the client's name on it.
//
// So Phase 0 is DRAFT MODE ONLY. `BotConfig.mode` exists as a field because the
// data model should not need reshaping in Phase 2 — but ⚠️ ONLY 'draft' IS
// HONOURED, and there is deliberately NO autonomous send path in the code.
// Autonomous is Phase 2, after the team has read ~100 real conversations.
//
// Collections (all server-only, default-deny):
//   botKnowledgeBases — per ACCOUNT. The business rundown the bot answers from.
//   botConfigs        — per BOT. Goals, tone, hours, caps, mode. Several bots
//                       per client is possible by design.
//   botConversations  — one per lead.
//   botMessages       — inbound + outbound, threaded to a conversation.
//
// AUDIT POSTURE (the community-inbox precedent exactly): INBOUND messages and
// AI-generated drafts are DOC-ONLY — an inbound SMS is an external mirror, and
// a draft nobody approved never reached anyone. OUTBOUND SENDS, APPROVALS,
// REJECTIONS, HANDOFFS and CONFIG/KB CHANGES are AUDITED, and ⚠️ the actor on a
// send is the PERSON WHO APPROVED IT, never the bot.

// ---------------------------------------------------------------------------
// Bot config
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ 'autonomous' IS REAL AS OF THE 2026-08-23 TRIAL BUILD, but it is bounded
 * by `testPhoneAllowlist` below rather than by this field alone. Setting the
 * mode with an empty allowlist REFUSES to send — see `resolveAutonomy` in
 * src/lib/leadBot/mode.ts, which is the single place that decides.
 */
export type BotMode = 'draft' | 'autonomous';

export const BOT_MODES: BotMode[] = ['draft', 'autonomous'];

/**
 * ⚠️⚠️ LAUNCH STATUS (club onboarding, 2026-11-04): whether this bot may speak to REAL leads at all. It is
 * separate from `mode`, which means "supervised or not", never "launched or not". Decided in one place,
 * src/lib/leadBot/launch.ts, and enforced at the send gate (`resolveAutonomy`), drafting, inbound routing,
 * Speed to Lead intake and the follow-up sweep.
 *   setting_up  nothing is sent to real leads; only `testPhoneAllowlist` numbers are answered.
 *   testing     the same, as a deliberate "we are trying it" step.
 *   live        real leads are answered, how depends on `replyMode`.
 *   on_hold     UI label "Paused". The bot sends nothing; inbound is recorded and a person is told.
 * ⚠️ NOT 'paused': that is already a send-block reason meaning "a person has the texts" (staff takeover).
 * An ABSENT value on a stored doc is read as the legacy behaviour (see `resolveLaunch`), so deploy and the
 * migration can happen in either order.
 */
export type BotLaunchStatus = 'setting_up' | 'testing' | 'live' | 'on_hold';
export const BOT_LAUNCH_STATUSES: BotLaunchStatus[] = ['setting_up', 'testing', 'live', 'on_hold'];

/**
 * ⚠️⚠️ WHAT "LIVE" MEANS FOR REAL LEADS (Scott, 2026-11-04). 'approve': a person approves each reply (a draft).
 * 'auto': the assistant texts real leads on its own. New clubs go live on 'approve'; switching to 'auto' is a
 * Kyck-admin-only, confirmed and audited action during the pilot.
 */
export type BotReplyMode = 'approve' | 'auto';
export const BOT_REPLY_MODES: BotReplyMode[] = ['approve', 'auto'];

/** Days are 0=Sunday … 6=Saturday, matching `Date.getDay()`. */
export interface BusinessHoursWindow {
  day: number;
  /** Local wall-clock "HH:MM", inclusive. */
  openTime: string;
  /** Local wall-clock "HH:MM", exclusive. */
  closeTime: string;
}

export interface BotBusinessHours {
  /** IANA zone, e.g. 'Australia/Brisbane'. The client's local time, not ours. */
  timezone: string;
  windows: BusinessHoursWindow[];
}

/**
 * Runaway protection, CONFIGURABLE PER BOT. ⚠️ These are not tuning knobs —
 * they are the ceiling on how badly a loop can go wrong while nobody is
 * watching. An auto-responder ping-pong would burn money and look deranged to
 * whoever eventually reads the transcript.
 *
 * ⚠️⚠️ THERE IS NO UNLIMITED, AND ZERO IS NOT ONE. Both are positive integers
 * within bounds; blank and zero are refused, at the schema AND on read. Same
 * reasoning as the allowlist — an off switch on the circuit breaker must not be
 * one keystroke away on a settings screen. Set a high number instead. See
 * CAP_BOUNDS / resolveRunawayCaps in src/lib/leadBot/runaway.ts.
 */
export interface BotRunawayCaps {
  /** Hard stop on outbound messages SENT in one conversation. */
  maxMessagesPerConversation: number;
  /** Hard stop on outbound messages per rolling hour, across the bot. */
  maxOutboundPerHour: number;
}

export interface BotConfig {
  id: string;
  accountId: string;
  /**
   * The number NEW conversations of this bot text from (E.164), stamped onto each conversation at
   * creation as `fromNumber`. Absent = the Kyck Twilio number, exactly as before. The provider is
   * a fact about the NUMBER (messaging registry), never a global switch. Set only through the audited
   * `setBotSmsFromNumber`; existing conversations keep the number they started on.
   */
  smsFromNumber?: string | null;
  /**
   * The booking calendar this bot offers times from (an enabled, non-archived calendar on the SAME account).
   * Unset + exactly one enabled calendar on the account = that one; unset + several = no times offered.
   */
  bookingCalendarId?: string | null;
  accountName: string | null;
  knowledgeBaseId: string | null;
  /** Human label — several bots per client is possible by design. */
  name: string;
  mode: BotMode;
  isActive: boolean;
  /** Absent on a legacy doc: read through `resolveLaunch`, never directly. */
  launchStatus?: BotLaunchStatus;
  /** Only meaningful while `launchStatus` is 'live'. Absent = 'approve'. */
  replyMode?: BotReplyMode;
  /** How the assistant introduces itself in the disclosure message. */
  assistantName: string;
  /** What a good conversation achieves — a call, a showroom visit. */
  goal: string;
  /** Free text; passed to the model verbatim. */
  tone: string;
  businessHours: BotBusinessHours;
  /**
   * ⚠️⚠️ THE GUARD THE AUTONOMOUS TRIAL RESTS ON. E.164 numbers this bot may
   * text WITHOUT REVIEW, and — while it is non-empty — the only numbers whose
   * inbound messages reach the bot at all.
   *
   * It exists because the trial runs autonomous sending on the EXISTING SHARED
   * BROADCAST NUMBER, where real franchisee contacts already reply. Without it
   * the bot would answer a franchisee's "thanks" with RV sales conversation,
   * unprompted and unreviewed. See src/lib/leadBot/allowlist.ts.
   *
   * ⚠️ EMPTY DOES NOT MEAN "ANYONE". An autonomous bot with an empty allowlist
   * refuses to send. Going live for real takes TWO deliberate acts — a
   * dedicated number AND a cleared allowlist — so that clearing this field on
   * its own stops the bot rather than unleashing it.
   */
  testPhoneAllowlist: string[];
  /**
   * ⚠️ EXTRA CONTEXT FOR THIS BOT ON TOP OF ITS ACCOUNT'S KNOWLEDGE BASE.
   * ADDITIVE, NEVER A REPLACEMENT — the account KB is the base. Fenced as
   * untrusted data in the prompt exactly as the account KB is, for the same
   * reason: whoever can edit it must not be able to rewrite the bot's rules.
   */
  knowledgeSupplement: string;
  /**
   * ⚠️ Where a handoff or callback request is NOTIFIED. E.164, optional.
   * When unset a handoff is still RECORDED and visible in the queue — it simply
   * is not paged. A handoff that evaporates because a config field was blank is
   * worse than one nobody was paged about.
   */
  forwardingNumber: string | null;
  handoffContactName: string | null;
  handoffPhone: string | null;
  handoffEmail: string | null;
  caps: BotRunawayCaps;
  /** Stalled-conversation handoff: outbound turns with no progress. */
  maxTurnsWithoutProgress: number;

  // ---- FITNESS VERTICAL (Run N, 2026-10-15). All optional: a doc without them
  // is an AUTOMOTIVE bot, exactly as before, which is what keeps every existing
  // test and every existing doc untouched. -----------------------------------
  /** Selects the rule pack, the detector and the knowledge source. Absent = automotive. */
  vertical?: BotVertical;
  playbook?: BotPlaybook;
  /** What it says to "is this a bot?" (brief 4.1). Blank = the shipped default. */
  disclosureAnswer?: string;
  /** Sign-off used at the end of messages (brief 4.1). */
  signOff?: string;
  /** Picks from the profile's team rows: the handoff targets. */
  handoffMemberIds?: string[];
  /**
   * ⚠️ EACH CAPABILITY GATES DATA, NOT INSTRUCTIONS (principle 1). `discussPromotions`
   * off means no promotions block in the prompt, not "please don't discuss them".
   */
  capabilities?: BotCapabilities;
  /** ⚠️ Stored only. Voice handling is post-conference — see the Run O brief 4. */
  callForwardNumber?: string | null;
  /** Run O: follow-up cadence. */
  followUps?: BotFollowUps;
  /**
   * ⚠️⚠️ "INSTRUCTIONS FOR SAM (KYCK ONLY)" - how this bot BEHAVES, written by Kyck staff
   * and never by the client. It reaches the prompt as INSTRUCTIONS, outside the untrusted
   * business-information fence, which is only safe because a client cannot write it: never
   * host it in the client portal. It is NOT a fact source: prices, hours and offers belong
   * in the Business Profile, and a figure written here is never in the price verifier's
   * allowed corpus. It cannot loosen a hard rule: the RULES above it in the prompt win, and
   * the hard stops, verifier, distress, opt-out and demo expiry live in code.
   */
  instructions?: string;
  instructionsUpdatedAt?: IsoTimestamp | null;
  instructionsUpdatedBy?: string | null;
  instructionsUpdatedByName?: string | null;
  /**
   * ⚠️ CONFERENCE MODE (Run P). A demo bot may hold a larger allowlist and longer
   * invite links — but ONLY while `demoExpiresAt` is in the future. Past it the
   * allowlist reads as EMPTY (one function, read time), which returns the shared
   * number to normal broadcast routing. See src/lib/leadBot/demo.ts.
   */
  isDemo?: boolean;
  demoExpiresAt?: IsoTimestamp | null;
  /** Demo only: scale follow-up delays (1 day becomes N minutes). */
  demoTimeScale?: number | null;

  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
  updatedBy: string;
}

/**
 * ⚠️ A UNION WITH AN EXHAUSTIVE SWITCH (the lead-relay provider-seam pattern): a
 * third vertical will not compile until src/lib/leadBot/verticals.ts handles it.
 * Never scatter `if (vertical === 'fitness')` through the send path.
 */
export type BotVertical = 'automotive' | 'fitness';
export const BOT_VERTICALS: BotVertical[] = ['automotive', 'fitness'];

/** A union of one for now; reactivation is the candidate second. */
export type BotPlaybook = 'new_lead_engagement';

export interface BotCapabilities {
  discussPromotions: boolean;
  /** Stored in Run N, does something from Run O. */
  bookAppointments: boolean;
  answerFaqs: boolean;
  handleObjections: boolean;
}

export const DEFAULT_CAPABILITIES: BotCapabilities = {
  discussPromotions: true,
  bookAppointments: false,
  answerFaqs: true,
  handleObjections: true,
};

export interface FollowUpStep {
  enabled: boolean;
  /** Minutes since the LAST message in the conversation, either side. */
  delayMinutes: number;
  /** The club's own words. Tokens: {{FirstName}} {{AssistantName}} {{ClubName}} {{Offer}} {{OfferEnds}} */
  message: string;
}

export interface BotFollowUps {
  /** The lead never replied. Starts after the opening message. */
  noResponse: FollowUpStep[];
  /** The lead replied, went quiet, and the bot's last message left something open. */
  incomplete: FollowUpStep[];
  /** Local wall-clock "HH:MM", in the club's timezone. Follow-ups only. */
  window: { start: string; end: string };
  /** ⚠️ Server-managed (copied setup): the follow-ups were copied from another club and a person has not checked them yet. */
  copiedFrom?: { accountId: string; accountName: string; at: IsoTimestamp } | null;
  needsReview?: boolean;
}

export type FollowUpSequence = 'no_response' | 'incomplete';

/** What the bot has learned about a lead — produced by the reply call, no extra cost. */
export interface LeadFacts {
  goal: string;
  motivation: string;
  preferredTimes: string;
  objections: string;
  readiness: 'cold' | 'warm' | 'hot';
}

// ---------------------------------------------------------------------------
// Knowledge base
// ---------------------------------------------------------------------------

/**
 * ⚠️ EVERY FIELD HERE IS UNTRUSTED CONTENT as far as the prompt is concerned.
 * It is written by a human into a form, so it is not hostile by design — but it
 * is not part of the system prompt either, and the prompt labels it as data with
 * an explicit instruction to ignore anything inside it that reads like a
 * command. The caption-drafting posture, for the same reason: the alternative is
 * that whoever can edit a KB field can rewrite the bot's rules.
 */
export interface BotKnowledgeBase {
  id: string;
  accountId: string;
  accountName: string | null;
  businessName: string;
  about: string;
  addresses: string;
  hours: string;
  brandsStocked: string;
  services: string;
  financePolicy: string;
  tradeInPolicy: string;
  warranty: string;
  viewingProcess: string;
  extraNotes: string;
  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
  updatedBy: string;
}

/** The free-text sections, in the order the form and the prompt render them. */
export type BotKnowledgeBaseSectionKey =
  | 'about'
  | 'addresses'
  | 'hours'
  | 'brandsStocked'
  | 'services'
  | 'financePolicy'
  | 'tradeInPolicy'
  | 'warranty'
  | 'viewingProcess'
  | 'extraNotes';

export const KNOWLEDGE_BASE_SECTIONS: ReadonlyArray<{
  key: BotKnowledgeBaseSectionKey;
  label: string;
}> = [
  { key: 'about', label: 'About the business' },
  { key: 'addresses', label: 'Addresses' },
  { key: 'hours', label: 'Opening hours' },
  { key: 'brandsStocked', label: 'Brands stocked' },
  { key: 'services', label: 'Services offered' },
  { key: 'financePolicy', label: 'Finance policy' },
  { key: 'tradeInPolicy', label: 'Trade-in policy' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'viewingProcess', label: 'What happens at a viewing' },
  { key: 'extraNotes', label: 'Anything else' },
];

// ---------------------------------------------------------------------------
// Handoff
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ `compliance` IS THE ONE WITH LEGAL EXPOSURE. A live Camperagent listing
 * reads "you only need a car licence to drive it" — a legal claim that depends
 * on that unit's 4,490kg GVM AND on the customer's own licence class. Licence
 * eligibility, towing capacity and weight compliance are an IMMEDIATE handoff,
 * every time. The bot may quote what a listing says; it may not advise. Get this
 * wrong and someone drives or tows illegally on advice carrying the client's
 * brand.
 */
export type HandoffTriggerKind =
  | 'compliance'
  | 'human_requested'
  | 'price_negotiation'
  | 'finance'
  | 'trade_in'
  | 'complaint'
  | 'booking_ready'
  | 'unknown_to_kb'
  | 'stalled'
  | 'manual'
  // FITNESS rule pack (Run N, 2026-10-15) — see src/lib/fitnessBot/hardStops.ts.
  | 'cancellation_billing'
  | 'injury_medical'
  | 'legal'
  | 'safety_incident'
  | 'media_corporate'
  | 'faq_handover'
  /** ⚠️ Not a handover: automation STOPS, top-priority alert, no sales content. */
  | 'distress'
  /** A conversational opt-out ("don't text me again"), same as the STOP keyword. */
  | 'opt_out'
  /** The price verifier refused a drafted reply (an unlisted figure). */
  | 'price_unverified'
  /** The reply was replaced by the club's not-sure message. */
  | 'not_sure'
  /** Run U: the conversation reached its reply limit (the lead got one fixed hand-over message). */
  | 'reply_cap'
  /** Run U: the bot-wide hourly reply limit was hit (system brake; the lead gets nothing). */
  | 'hourly_cap'
  /** Run U: drafting or sending genuinely failed. NOT a person handing over ('manual'). */
  | 'drafting_error'
  /** Run U: the lead's message looks like an auto-reply, so the assistant stopped. */
  | 'automated_counterparty'
  /** Anytime Fitness booking could not be completed by the assistant (a person must lock the time in). */
  | 'booking_failed';

export interface HandoffTrigger {
  kind: HandoffTriggerKind;
  /** Human-readable: what fired, in words an AM can act on. */
  detail: string;
  /**
   * Which layer caught it. ⚠️ Both matter: `rule` is our own deterministic
   * detector (a net, not a proof), `model` is Claude's own classification, and
   * `manual` is a human. They are UNIONED — see src/lib/leadBot/handoff.ts.
   */
  source: 'rule' | 'model' | 'manual';
  /** Only on `booking_failed`: the Anytime Fitness booking cause code (src/lib/appointments/afHandoverCauses.ts), so every surface can say WHY. */
  cause?: string;
}

/**
 * ⚠️ Two modes, and the distinction belongs in the model rather than being
 * inferred later: the bot cannot hand off to a sleeping salesperson. In hours,
 * notify. Out of hours, capture and queue ("I'll have someone call you first
 * thing — what time suits?").
 */
export type HandoffMode = 'notify' | 'queue';

// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------

export type BotConversationState =
  | 'active'
  | 'handoff'
  | 'closed'
  /** The lead sent STOP. ⚠️ Terminal for outbound — nothing sends, ever. */
  | 'stopped';

export const BOT_CONVERSATION_STATES: BotConversationState[] = [
  'active',
  'handoff',
  'closed',
  'stopped',
];

/**
 * ⚠️⚠️ THE OUTCOME (Speed to Lead Stage 4.5): there is ONE outcome list, in `src/types/leadOutcome.ts` (code -> label ->
 * stage). A conversation keeps its own record of what came of it (`BotConversation.outcome`, shown in its history); the LEAD
 * has one outcome per club (src/types/lead.ts), which AI and system suggestions move forward only and a person can set.
 * Nothing here defines a second list.
 */

export type BotLeadSource = 'website' | 'meta_lead_form' | 'phone' | 'walk_in' | 'other';

export const BOT_LEAD_SOURCES: BotLeadSource[] = [
  'website',
  'meta_lead_form',
  'phone',
  'walk_in',
  'other',
];

/**
 * The AI summary and outcome SUGGESTION for a text conversation (2026-10-29). Written only by the classification sweep, never on
 * a page view. ⚠️ `suggestedOutcome` is a suggestion: a person confirms it, and it reaches the LEAD only through the forward-only
 * AI rule. `count` is the classifications used so far (max 3); `leadAt` is the newest lead message it had seen.
 */
export interface ConversationClassification {
  /** `pending` = claimed or rescheduled but no result yet. */
  status: 'pending' | 'ready' | 'failed' | 'skipped';
  summaryText: string | null;
  suggestedOutcome: LeadOutcome | null;
  confidence: 'low' | 'medium' | 'high' | null;
  nextStep: string | null;
  model: string | null;
  at: IsoTimestamp | null;
  count: number;
  attempts: number;
  reason: string | null;
  leaseUntil: IsoTimestamp | null;
  leadAt: IsoTimestamp | null;
}

export interface BotConversation {
  id: string;
  accountId: string;
  accountName: string | null;
  botConfigId: string;

  leadName: string;
  /** E.164. The routing key for inbound SMS. */
  leadPhone: string;
  leadEmail: string | null;
  source: BotLeadSource;
  campaign: string | null;
  /** Set when the lead matched an existing kyck contact. */
  contactId: string | null;

  state: BotConversationState;

  handoffTriggers: HandoffTrigger[];
  handoffMode: HandoffMode | null;
  handoffAt: IsoTimestamp | null;
  handoffNote: string | null;

  /** Every message, both directions. */
  messageCount: number;
  /** Outbound messages actually SENT (not drafted). The per-conversation cap
   *  counts these — a rejected draft costs the lead nothing. */
  outboundSentCount: number;
  lastInboundAt: IsoTimestamp | null;
  lastOutboundAt: IsoTimestamp | null;
  /** Doc-only mirror of the STOP that the audited contact flip already records. */
  stoppedAt: IsoTimestamp | null;
  /** The last STOP / conversational opt-out for this number, stamped on EVERY conversation of the phone. Doc-only. */
  optedOutAt?: IsoTimestamp | null;
  /** The last START for this number, stamped the same way. An opt-out older than this is cleared. Doc-only. */
  optedInAt?: IsoTimestamp | null;

  /**
   * ⚠️ What actually happened. See LeadOutcome — it cannot be
   * backfilled, so it is captured as the trial runs rather than reconstructed
   * afterwards. Legacy docs written before this field existed read as
   * `unresolved` at the store boundary, which is the honest value for them.
   */
  outcome: LeadOutcome;
  outcomeSetAt: IsoTimestamp | null;
  outcomeSetBy: string | null;
  outcomeSetByName: string | null;
  /** Set by the Stage 4.5 outcome migration when this conversation's old outcome was a status ("handed off"): the lead backfill reads it. */
  outcomeMigratedFrom?: string | null;
  /** When the AI summary / suggested outcome is next due (the minutely sweep). Null = nothing scheduled. Set by hooks only. */
  classifyDueAt?: IsoTimestamp | null;
  /** The last AI summary of this conversation and its suggested outcome. */
  classification?: ConversationClassification | null;

  // ---- FITNESS (Run N/O/P). Optional so legacy docs read unchanged. ---------
  /** Produced by the reply call; shown in ConversationDetail; feeds staff alerts. */
  leadFacts?: LeadFacts | null;
  /**
   * ⚠️ Set the moment distress is detected, BEFORE the (single) kind message is
   * sent. While set, NOTHING drafts and NOTHING follows up. The state flips to
   * 'stopped' once that message has gone, so the message itself is not blocked
   * by its own stop.
   */
  distressAt?: IsoTimestamp | null;
  stopReason?: 'opt_out' | 'distress' | null;
  /**
   * Set when a STOP to ONE OF OUR VONAGE NUMBERS stopped this conversation: what the state was just
   * before, so a START to the same number puts it back (never to a state it was not in). Doc-only.
   */
  preStopState?: BotConversationState | null;
  /**
   * 'number' = stopped by a (phone, OUR number) opt-out: it binds this number only, so it must NOT read
   * as the phone-wide legacy opt-out other numbers honour. Absent on every Twilio-era stop.
   */
  optOutScope?: 'number' | null;
  /** When the first outbound message was actually sent — the "time to first text". */
  firstOutboundAt?: IsoTimestamp | null;
  /** Run O follow-up scheduling. */
  nextFollowUpAt?: IsoTimestamp | null;
  followUpSequence?: FollowUpSequence | null;
  /** Index of the NEXT step to send within the sequence. */
  followUpStep?: number | null;
  followUpLeaseUntil?: IsoTimestamp | null;
  followUpsFinishedAt?: IsoTimestamp | null;
  /** Why a scheduled step did not send (e.g. an unresolvable token). */
  followUpSkips?: Array<{ step: number; reason: string; at: IsoTimestamp }>;
  /** Run O booking. */
  appointmentId?: string | null;
  /**
   * ⚠️ A verifier HOLD is not a handover: the lead gets the not-sure message, the conversation
   * stays open with the assistant, and staff are told AT MOST ONCE per conversation.
   */
  verifierHoldCount?: number;
  verifierHoldAlertedAt?: IsoTimestamp | null;
  /** When staff were last told the assistant couldn't answer something (an FYI, not a handover). At most one per 24h. */
  fyiAlertedAt?: IsoTimestamp | null;
  /** Staff gave the conversation back to the assistant after a handover (audited). */
  handedBackAt?: IsoTimestamp | null;
  handedBackBy?: string | null;
  /** The last reaction (tapback) the lead sent. It is not a reply: nothing answers it. */
  lastReactionAt?: IsoTimestamp | null;
  /** Arrived outside the club's staffed hours — for the booth dashboard. */
  outsideStaffedHours?: boolean;
  /** Demo only: lets "reset demo" find its own conversations. */
  isDemo?: boolean;
  /**
   * PROVIDER-PROOFING: the number this conversation texts FROM, stamped at creation. Every reply
   * (the assistant's or a person's) sends from it, so a new SMS provider or a dedicated number per
   * club is an adapter + config change, not a migration. Absent on older conversations, which read
   * as the current Kyck number (`conversationFromNumber`).
   */
  fromNumber?: string | null;
  /**
   * ⚠️ STAFF TAKEOVER: a person (club staff in the portal, or Kyck staff) replied, so the assistant
   * is paused on THIS conversation until "Hand back". Deliberately NOT the `handoff` state: a
   * handover is the assistant asking for a person, a takeover is a person having taken over.
   * Inbound messages still land; nothing is drafted and no follow-up is sent while it is set.
   */
  staffTakeoverAt?: IsoTimestamp | null;
  staffTakeoverBy?: string | null;
  staffTakeoverByName?: string | null;
  staffTakeoverSurface?: 'portal' | 'kyck' | null;
  /** The last time a person replied in this conversation (for "lead is waiting" in Needs attention). */
  lastStaffReplyAt?: IsoTimestamp | null;
  /** When the team was last told that the lead replied while a person had the texts (throttles that notice to one per 15 minutes). Doc-only. */
  pausedReplyNotifiedAt?: IsoTimestamp | null;

  /**
   * SPEED TO LEAD (Stage 2). Set when a conversation is created in STANDBY because both Sam and the call
   * channel are on for the club: NO opener and NO follow-ups, but an inbound text from the lead is still
   * answered. The fallback sweep clears it when it releases the opener; a lead the call reached leaves it
   * set for good. Absent on every other conversation.
   */
  speedToLeadStandbyAt?: IsoTimestamp | null;
  /** The lead intake event (= LeadSubmission id) that started this conversation, when it came from one. */
  intakeEventId?: string | null;
  /** When the enquiry that started this conversation arrived (the intake event's receipt time). Speed figures measure from it. Absent on older conversations. */
  enquiryReceivedAt?: IsoTimestamp | null;
  /** Speed to Lead: started from a TEST lead sent through intake (route editor). Shown as a test everywhere. */
  intakeTest?: boolean;
  /**
   * ANYTIME FITNESS BOOKING: the prospect AF holds for this lead at the club, captured by a booking-flow
   * lead-capture Sam ran ONCE because the lead had no relayed prospect. Stored so a second booking never
   * captures again. Doc-only; ids only.
   */
  afProspect?: {
    locationNumber: string;
    externalId: string;
    deepLinkUri: string | null;
    canBookAppointment: boolean | null;
    capturedAt: IsoTimestamp;
  } | null;

  /** DEMO DATA: the seed run id on a synthetic doc. Every outbound path refuses a doc that carries it (src/lib/demoData/demoGuard.ts). Absent on real docs. */
  demoSeed?: string;
  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * `system` is an EVENT on the thread (a missed-call note, the speed-to-lead call note): not something the lead said and
 * not something we sent. ⚠️ Never counts as a lead reply, a turn, an opening, or engagement. See messageKinds.ts.
 */
export type BotMessageDirection = 'inbound' | 'outbound' | 'system';

/**
 * ⚠️ `pending_approval` IS THE DEFAULT AND THE POINT. A draft sits here until a
 * human at kyck approves it. Nothing in Phase 0 moves a message out of this
 * state without an approving user id.
 */
export type BotMessageStatus =
  | 'received'
  | 'pending_approval'
  /**
   * ⚠️ A TRANSIENT CLAIM, not a state anyone sits in — the metaAds publish
   * precedent. The approval transition is a CAS from 'pending_approval' to
   * this, so a double-click or two AMs approving the same draft cannot both
   * reach Twilio: the second finds it already claimed. Always resolved to
   * 'sent' or 'failed' inside the same request.
   */
  | 'sending'
  | 'sent'
  | 'failed'
  /** A human read it and killed it. */
  | 'rejected'
  /**
   * ⚠️ A DISTINCT STATE FROM 'rejected', deliberately. When the lead texts
   * again before an AM has approved the previous draft, that draft is answering
   * a message that is no longer the last word — it is stale, not wrong, and
   * nobody judged it. Filing it as 'rejected' would put a decision in the
   * transcript that no human made.
   */
  | 'superseded';

/**
 * ⚠️ WHY A SEND WAS NEVER ATTEMPTED — distinct from `failureReason`, which means
 * it WAS attempted and the provider said no. Two different Tier-1 numbers on the
 * report: a cap hit is the safety machinery working, a provider failure is
 * something broken. Conflating them would hide both.
 */
export type BotSendBlockReason =
  | 'runaway'
  | 'stopped'
  | 'opted_out'
  | 'not_permitted'
  | 'paused'
  /** The club's bot is setting up or testing and this number is not a test number. */
  | 'not_launched'
  /** The club's bot is on hold ("Paused"): the assistant sends nothing. */
  | 'on_hold';

/**
 * What one draft cost to produce. ⚠️ Captured on the MESSAGE because the
 * `[claude]` usage line is a LOG — excellent for spotting a cache miss, useless
 * for aggregating a month later. Cost per conversation is a first-class question
 * for a trial deciding viability, and a log cannot answer it.
 *
 * ⚠️ ABSENT ON EVERY MESSAGE WRITTEN BEFORE THIS EXISTED, and on the fixed
 * compliance deflection (no model wrote it). The report counts what it has and
 * states the denominator rather than averaging over a partial set as though it
 * were complete.
 */
export interface BotMessageUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

export interface BotMessage {
  id: string;
  conversationId: string;
  accountId: string;
  direction: BotMessageDirection;
  status: BotMessageStatus;
  text: string;
  /** What the model wrote, kept when a human edits `text` before sending. */
  originalDraft: string | null;
  aiGenerated: boolean;
  model: string | null;
  /** Triggers detected on the inbound turn this draft answers. */
  handoffTriggers: HandoffTrigger[];
  /** Twilio message SID (outbound) or inbound SID. */
  externalMessageId: string | null;
  /** ⚠️ The send was ATTEMPTED and failed. See blockedReason for the other case. */
  failureReason: string | null;
  /**
   * ⚠️ The send was REFUSED before it was attempted — a runaway cap, a STOP, an
   * opt-out, or autonomy not being permitted. Doc-only telemetry: it changes no
   * gate, it records which gate fired, so the trial can see how often the safety
   * machinery actually engaged.
   */
  blockedReason: BotSendBlockReason | null;
  blockedAt: IsoTimestamp | null;
  /** What producing this draft cost. Null for a legacy message or a fixed deflection. */
  usage: BotMessageUsage | null;

  /**
   * ⚠️ NULL ON AN AUTONOMOUS SEND, and that is how the transcript knows a
   * message went out UNREVIEWED — see `wasSentUnreviewed`. Deliberately DERIVED
   * from "nobody approved it" rather than stored as its own boolean: the
   * absence of an approver IS the ground truth, and a separate flag could drift
   * from it.
   */
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: IsoTimestamp | null;
  rejectedBy: string | null;
  rejectedAt: IsoTimestamp | null;
  sentAt: IsoTimestamp | null;

  /**
   * FITNESS: what to do to the conversation AFTER this message has been sent (or
   * its send attempted). ⚠️ Applied AFTER, never before: stopping first would make
   * the stop block its own confirmation. `opt_out` also flips the contact's SMS
   * flag through the audited contacts path; both stop the conversation.
   */
  afterSend?: 'opt_out' | 'distress' | null;
  /** Fixed server wording rather than model output — the transcript says so. */
  fixedKind?: FixedMessageKind | null;
  /** Run O: this message is a scheduled follow-up (sequence + step index). */
  followUp?: { sequence: FollowUpSequence; step: number } | null;
  /**
   * The model's own judgement, at the moment it wrote this reply, on whether it
   * leaves the conversation open. Read by the scheduler when the message SENDS.
   * Absent on fixed messages and the opener (silence is the safe default).
   */
  followUpJudgement?: 'needed' | 'not_needed' | null;
  /** An inbound tapback ("Loved ..."): stored and shown as a reaction, never answered. */
  reaction?: boolean;
  /**
   * Inbound call forwarding: a one-line note that the lead PHONED the club (answered, or missed). Written
   * by the system, not typed by the lead, so the thread can say so. Doc-only.
   */
  callEvent?: boolean;
  /**
   * A reply typed by a PERSON (club staff in the portal, or Kyck staff), not by the assistant. The
   * actor's name is for the Kyck view only; the portal shows "Club team".
   */
  staffReply?: { surface: 'portal' | 'kyck'; byName: string | null } | null;

  /** DEMO DATA: the seed run id on a synthetic doc. Every outbound path refuses a doc that carries it (src/lib/demoData/demoGuard.ts). Absent on real docs. */
  demoSeed?: string;
  createdAt: IsoTimestamp;
}

export type FixedMessageKind =
  | 'handover'
  | 'distress'
  | 'opt_out_confirmation'
  | 'bot_disclosure'
  | 'not_sure'
  | 'booking_confirmation'
  | 'slot_taken'
  | 'follow_up'
  | 'reply_cap';

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

export interface BotConversationDetail {
  conversation: BotConversation;
  messages: BotMessage[];
  /** The one draft awaiting approval, if any. */
  pendingDraft: BotMessage | null;
  config: BotConfig | null;
  /** The tour booked from this conversation, when there is one (staff view: provider badge, staff initials, slot-gone evidence). */
  appointment?: Appointment | null;
}

/**
 * The outcome of an approve-and-send. ⚠️ `ok: false` with reason
 * 'not_configured' is a NORMAL, non-error state — the bot's Twilio number is
 * provisioned separately, and until it exists the whole queue must still work
 * rather than 5xx-ing.
 */
export type BotSendOutcome =
  | { ok: true; message: BotMessage }
  | {
      ok: false;
      reason:
        | 'not_configured'
        | 'stopped'
        | 'runaway'
        | 'opted_out'
        | 'provider_error'
        /** Autonomous send refused — draft mode, empty allowlist, or not allowlisted. */
        | 'not_permitted'
        /** A person has the texts for this lead (staff takeover): the assistant sends nothing until it is handed back. */
        | 'paused';
      detail: string;
      message: BotMessage | null;
      /** Which gate refused, when `reason` is 'not_permitted' (typed: callers map it, they never read `detail`). */
      autonomyReason?: AutonomyBlockReason;
    };

/**
 * ⚠️ Did this message go out WITHOUT a human reading it? The transcript has to
 * make that obvious, because "the bot said this" and "a person read this and
 * sent it" are different facts about the same text — and after the approval
 * step was removed, both now appear in the same thread.
 *
 * Derived from the absence of an approver rather than from a stored flag: that
 * absence is the ground truth, and every Phase 0 send carries an approver, so
 * the derivation is sound for historical messages too.
 */
export function wasSentUnreviewed(message: BotMessage): boolean {
  return message.status === 'sent' && message.approvedBy === null;
}
